import React, { useState, useEffect } from 'react';
import { Mail, Save, RefreshCw, Loader2, Edit2, Eye, X, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import AdminLogin from './AdminLogin';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../hooks/useAdminAuth';
import DOMPurify from 'dompurify';

import { apiUrl, API_URL } from '../utils/config';
import logger from '../utils/logger';

// Default email templates
const DEFAULT_TEMPLATES = [
  {
    id: 'contact_notification',
    name: 'Contact Form Notification',
    description: 'Email sent to admin when someone submits the contact form',
    subject: 'New CoachMeUpp Inquiry from {{fullName}} ({{company}})',
    body: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h2 style="color: #1e40af;">New Contact Form Submission</h2>
  <div style="background: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0;">
    <p><strong>Name:</strong> {{fullName}}</p>
    <p><strong>Email:</strong> <a href="mailto:{{email}}">{{email}}</a></p>
    <p><strong>Company:</strong> {{company}}</p>
    <p><strong>Job Title:</strong> {{jobTitle}}</p>
    <p><strong>Company Size:</strong> {{companySize}}</p>
    <p><strong>Interest:</strong> {{interest}}</p>
  </div>
  <div style="background: #fff; padding: 20px; border: 1px solid #e5e7eb; border-radius: 8px;">
    <h3>Message:</h3>
    <p>{{message}}</p>
  </div>
  <p style="color: #6b7280; font-size: 12px; margin-top: 20px;">Submission ID: {{submissionId}}</p>
</div>`,
    variables: ['fullName', 'email', 'company', 'jobTitle', 'companySize', 'interest', 'message', 'submissionId']
  },
  {
    id: 'demo_booking_notification',
    name: 'Demo Booking Notification',
    description: 'Email sent to admin when someone books a demo',
    subject: '🎯 ACTION REQUIRED: Demo Request from {{fullName}} at {{company}}',
    body: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <div style="background: #dc2626; color: white; padding: 15px; border-radius: 8px 8px 0 0;">
    <h2 style="margin: 0;">🎯 New Demo Booking Request</h2>
  </div>
  <div style="background: #fef2f2; padding: 20px; border: 1px solid #fecaca; border-radius: 0 0 8px 8px;">
    <p><strong>Name:</strong> {{fullName}}</p>
    <p><strong>Email:</strong> <a href="mailto:{{email}}">{{email}}</a></p>
    <p><strong>Company:</strong> {{company}}</p>
    <p><strong>Job Title:</strong> {{jobTitle}}</p>
    <p><strong>Company Size:</strong> {{companySize}}</p>
    <p><strong>Interest:</strong> {{interest}}</p>
  </div>
  <div style="background: #fff; padding: 20px; border: 1px solid #e5e7eb; border-radius: 8px; margin-top: 20px;">
    <h3>Message:</h3>
    <p>{{message}}</p>
  </div>
  <p style="color: #6b7280; font-size: 12px; margin-top: 20px;">Booking ID: {{bookingId}}</p>
</div>`,
    variables: ['fullName', 'email', 'company', 'jobTitle', 'companySize', 'interest', 'message', 'bookingId']
  },
  {
    id: 'career_application_notification',
    name: 'Career Application Notification',
    description: 'Email sent to admin when someone applies for a job',
    subject: '📋 New Application: {{positionTitle}} - {{fullName}}',
    body: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h2 style="color: #1e40af;">New Career Application</h2>
  <div style="background: #fef3c7; padding: 15px; border-radius: 8px; margin: 20px 0;">
    <p style="margin: 0;"><strong>Position:</strong> {{positionTitle}}</p>
  </div>
  <div style="background: #f3f4f6; padding: 20px; border-radius: 8px;">
    <p><strong>Name:</strong> {{fullName}}</p>
    <p><strong>Email:</strong> <a href="mailto:{{email}}">{{email}}</a></p>
    <p><strong>Phone:</strong> {{phone}}</p>
    <p><strong>LinkedIn:</strong> <a href="{{linkedin}}">{{linkedin}}</a></p>
  </div>
  <div style="background: #fff; padding: 20px; border: 1px solid #e5e7eb; border-radius: 8px; margin-top: 20px;">
    <h3>Cover Letter:</h3>
    <p>{{coverLetter}}</p>
  </div>
  <p style="color: #6b7280; font-size: 12px; margin-top: 20px;">Application ID: {{applicationId}}</p>
</div>`,
    variables: ['fullName', 'email', 'phone', 'linkedin', 'positionTitle', 'coverLetter', 'applicationId']
  },
  {
    id: 'resource_download_notification',
    name: 'Resource Download Notification',
    description: 'Email sent to admin when someone downloads a resource',
    subject: '📥 Resource Download: {{resourceTitle}} by {{name}}',
    body: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h2 style="color: #7c3aed;">Resource Download</h2>
  <div style="background: #f5f3ff; padding: 20px; border-radius: 8px; margin: 20px 0;">
    <p><strong>Resource:</strong> {{resourceTitle}}</p>
    <p><strong>Downloaded by:</strong> {{name}}</p>
    <p><strong>Email:</strong> <a href="mailto:{{email}}">{{email}}</a></p>
    <p><strong>Company:</strong> {{company}}</p>
  </div>
  <p style="color: #6b7280; font-size: 12px;">Download ID: {{downloadId}}</p>
</div>`,
    variables: ['name', 'email', 'company', 'resourceTitle', 'downloadId']
  }
];

const EmailTemplateAdmin = ({ onBackToHome }) => {
  const { isAuth, isCheckingAuth, handleLogin, handleLogout } = useAdminAuth();
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [isLoading, setIsLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isAuth) {
      fetchTemplates();
    }
  }, [isAuth]);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(apiUrl("email-templates"));
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          // Merge with defaults (in case new templates are added)
          const mergedTemplates = DEFAULT_TEMPLATES.map(defaultTpl => {
            const saved = data.find(d => d.id === defaultTpl.id);
            return saved ? { ...defaultTpl, ...saved } : defaultTpl;
          });
          setTemplates(mergedTemplates);
        }
      }
    } catch (error) {
      logger.error('Error fetching templates:', error);
      // Use defaults if fetch fails
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    setIsSaving(true);
    try {
      const response = await fetch(apiUrl("email-templates/${editingTemplate.id}"), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: editingTemplate.subject,
          body: editingTemplate.body
        })
      });

      if (response.ok) {
        toast.success('Template saved successfully!');
        setTemplates(prev => prev.map(t => 
          t.id === editingTemplate.id ? editingTemplate : t
        ));
        setEditingTemplate(null);
      } else {
        toast.error('Failed to save template');
      }
    } catch (error) {
      logger.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetTemplate = (templateId) => {
    const defaultTemplate = DEFAULT_TEMPLATES.find(t => t.id === templateId);
    if (defaultTemplate) {
      setEditingTemplate(defaultTemplate);
      toast.success('Template reset to default');
    }
  };

  const renderPreview = (template) => {
    // Replace variables with sample data
    let previewHtml = template.body;
    let previewSubject = template.subject;
    
    const sampleData = {
      fullName: 'John Smith',
      name: 'John Smith',
      email: 'john@example.com',
      company: 'Acme Corp',
      jobTitle: 'HR Director',
      companySize: '500-1000',
      interest: 'Enterprise Coaching',
      message: 'We are interested in implementing a coaching program for our leadership team.',
      submissionId: 'CNT-123456',
      bookingId: 'DMO-123456',
      applicationId: 'APP-123456',
      downloadId: 'DWN-123456',
      positionTitle: 'Senior Coach',
      phone: '+1 555-123-4567',
      linkedin: 'https://linkedin.com/in/johnsmith',
      coverLetter: 'I am excited to apply for this position...',
      resourceTitle: 'Executive Coaching ROI Guide'
    };

    template.variables.forEach(variable => {
      const regex = new RegExp(`{{${variable}}}`, 'g');
      previewHtml = previewHtml.replace(regex, sampleData[variable] || `[${variable}]`);
      previewSubject = previewSubject.replace(regex, sampleData[variable] || `[${variable}]`);
    });

    return { subject: previewSubject, body: previewHtml };
  };

  // Show loading while checking auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuth) {
    return <AdminLogin onLogin={handleLogin} title="Email Templates" />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader
        title="Email Templates"
        onBackToHome={onBackToHome}
        onLogout={handleLogout}
        showAddButton={false}
      />

      <div className="container mx-auto px-6 py-8">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-800 font-medium">Email Template Customization</p>
            <p className="text-blue-600 text-sm">
              Customize the email notifications sent when users submit forms. Use variables like <code className="bg-blue-100 px-1 rounded">{'{{fullName}}'}</code> to insert dynamic content.
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="grid gap-6">
            {templates.map((template) => (
              <div
                key={template.id}
                className="bg-white rounded-xl shadow-md overflow-hidden"
                data-testid={`template-card-${template.id}`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{template.name}</h3>
                        <p className="text-sm text-gray-500">{template.description}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPreviewTemplate(template)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setEditingTemplate(template)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Subject:</strong> {template.subject}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs text-gray-500">Variables:</span>
                      {template.variables.map((variable) => (
                        <span
                          key={variable}
                          className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded"
                        >
                          {`{{${variable}}}`}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Edit Email Template</h3>
                <p className="text-sm text-gray-500">{editingTemplate.name}</p>
              </div>
              <button
                onClick={() => setEditingTemplate(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Subject
                </label>
                <Input
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Email subject line"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use variables like {'{{fullName}}'} to personalize
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Body (HTML)
                </label>
                <Textarea
                  value={editingTemplate.body}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, body: e.target.value }))}
                  rows={15}
                  className="font-mono text-sm"
                  placeholder="HTML email content"
                />
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Available Variables:</p>
                <div className="flex flex-wrap gap-2">
                  {editingTemplate.variables.map((variable) => (
                    <button
                      key={variable}
                      onClick={() => {
                        navigator.clipboard.writeText(`{{${variable}}}`);
                        toast.success(`Copied {{${variable}}} to clipboard`);
                      }}
                      className="px-2 py-1 bg-white border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-100 transition-colors"
                    >
                      {`{{${variable}}}`}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-between">
              <Button
                variant="outline"
                onClick={() => handleResetTemplate(editingTemplate.id)}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset to Default
              </Button>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveTemplate}
                  disabled={isSaving}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Template
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Email Preview</h3>
                <p className="text-sm text-gray-500">{previewTemplate.name}</p>
              </div>
              <button
                onClick={() => setPreviewTemplate(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <div className="mb-4 p-3 bg-gray-100 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Subject:</strong> {renderPreview(previewTemplate).subject}
                </p>
              </div>
              <div 
                className="border border-gray-200 rounded-lg p-4"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderPreview(previewTemplate).body) }}
              />
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end">
              <Button onClick={() => setPreviewTemplate(null)}>
                Close Preview
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplateAdmin;
